// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {SettingsTabContainer, SettingsTabContent} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import Notification from '@app/features/ui/state/Notification';
import Sound from '@app/features/ui/state/Sound';
import * as UserSettingsCommands from '@app/features/user/commands/UserSettingsCommands';
import {Notifications} from '@app/features/user/components/modals/tabs/notifications_tab/Notifications';
import {PushSettings} from '@app/features/user/components/modals/tabs/notifications_tab/PushSettings';
import {Sounds} from '@app/features/user/components/modals/tabs/notifications_tab/Sounds';
import {TextToSpeech} from '@app/features/user/components/modals/tabs/notifications_tab/TextToSpeech';
import {useSoundSettings} from '@app/features/user/components/modals/tabs/notifications_tab/useSoundSettings';
import UserSettings from '@app/features/user/state/UserSettings';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const NotificationsTab: React.FC = observer(() => {
	const {t} = useLingui();
	const browserNotificationsEnabled = Notification.browserNotificationsEnabled;
	const unreadMessageBadgeEnabled = Notification.unreadMessageBadgeEnabled;
	const soundSettings = Sound.settings;
	const {afkTimeout} = UserSettings;
	const {
		soundTypeLabels,
		customSounds,
		handleToggleAllSounds,
		handleToggleSound,
		handleEnableAllSounds,
		handleDisableAllSounds,
		handlePreviewSound,
		handleUploadClick,
		handleCustomSoundDelete,
		handleMasterVolumeChange,
		handleSoundOverrideChange,
		handleSoundOverrideReset,
		handleAllOverridesReset,
	} = useSoundSettings();
	const handleAfkTimeoutChange = async (value: number) => {
		try {
			await UserSettingsCommands.update({afkTimeout: value * 60});
		} catch {}
	};
	return (
		<SettingsTabContainer>
			<SettingsTabContent>
				<SettingsSection id="notifications" title={t`Notifications`}>
					<Notifications
						browserNotificationsEnabled={browserNotificationsEnabled}
						unreadMessageBadgeEnabled={unreadMessageBadgeEnabled}
					/>
				</SettingsSection>
				<SettingsSection id="sounds" title={t`Sounds`}>
					<Sounds
						soundSettings={soundSettings}
						soundTypeLabels={soundTypeLabels}
						customSounds={customSounds}
						isSoundEnabled={Sound.isSoundTypeEnabled}
						onToggleAllSounds={handleToggleAllSounds}
						onToggleSound={handleToggleSound}
						onEnableAllSounds={handleEnableAllSounds}
						onDisableAllSounds={handleDisableAllSounds}
						onPreviewSound={handlePreviewSound}
						onUploadClick={handleUploadClick}
						onCustomSoundDelete={handleCustomSoundDelete}
						onMasterVolumeChange={handleMasterVolumeChange}
						onSoundOverrideChange={handleSoundOverrideChange}
						onSoundOverrideReset={handleSoundOverrideReset}
						onAllOverridesReset={handleAllOverridesReset}
					/>
				</SettingsSection>
				<SettingsSection
					id="text-to-speech"
					title={t`Text-to-speech`}
					description={t`Control speech commands and narration for incoming content.`}
				>
					<TextToSpeech />
				</SettingsSection>
				<SettingsSection id="push" title={t`Push Settings`}>
					<PushSettings afkTimeout={afkTimeout} onAfkTimeoutChange={handleAfkTimeoutChange} />
				</SettingsSection>
			</SettingsTabContent>
		</SettingsTabContainer>
	);
});

export default NotificationsTab;
